import { useCallback, useEffect, useState } from "react";
import { Scale, Gavel, Loader2, FileText, Banknote, Timer } from "lucide-react";
import api from "@/lib/api";
import { DEMO } from "@/components/cef/cefShared";

/* Courthouse inside the MDT — every unit reads the docket; judge-role sessions
   get the full bench (pleas display, call hearing, deliver verdict) without
   leaving the tablet. Shares the courthouse pipeline (fines ride the bank queue). */

const SEV_CLS = { infraction: "text-zinc-400 border-white/15", misdemeanor: "text-amber-300 border-amber-500/40", felony: "text-red-400 border-red-500/50" };
const STATUS_CLS = { awaiting_hearing: "text-amber-300 border-amber-500/40", in_hearing: "text-sky-300 border-sky-500/40 bg-sky-500/10", verdict: "text-emerald-300 border-emerald-500/40", warrant: "text-red-400 border-red-500/50" };
const fmt = (n) => `$${Number(n ?? 0).toLocaleString()}`;

export default function CourtView({ serverId, token, demo, sfx }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verdict, setVerdict] = useState({ outcome: "guilty", fine: "", jail_seconds: "", notes: "", judge_name: "" });

  const load = useCallback(() => {
    if (demo) { setData(DEMO.court); setErr(""); return; }
    api.get(`/nc/servers/${serverId}/court/docket`, { params: { token } })
      .then((r) => { setData(r.data); setErr(""); })
      .catch((e) => setErr(e.response?.data?.detail || "Courthouse offline"));
  }, [demo, serverId, token]);

  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  const openCase = (id) => {
    sfx.keyTick();
    if (demo) { setSel(DEMO.courtCase[id] || null); return; }
    api.get(`/nc/servers/${serverId}/court/cases/${id}`, { params: { token } }).then((r) => setSel(r.data)).catch(() => {});
  };

  const act = async (path, body) => {
    if (demo) { import("sonner").then(({ toast }) => toast.info("Preview mode — verdicts bind in-game")); return; }
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${serverId}/court/cases/${sel.id}/${path}`, body, { params: { token } });
      import("sonner").then(({ toast }) => toast.success(r.data.message));
      load(); openCase(sel.id);
    } catch (e) { import("sonner").then(({ toast }) => toast.error(e.response?.data?.detail || "Failed")); }
    finally { setBusy(false); }
  };

  if (err) return <div className="flex-1 flex items-center justify-center"><p data-testid="mdt-court-error" className="font-mono text-[11px] text-red-400">{err}</p></div>;
  if (!data) return <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-sky-400" /></div>;

  const canJudge = data.viewer?.is_judge;

  return (
    <div className="flex-1 flex min-h-0" data-testid="mdt-court">
      {/* docket */}
      <aside className="w-[280px] shrink-0 border-r border-white/10 overflow-y-auto bg-black/40" style={{ scrollbarWidth: "thin" }}>
        <p className="px-3.5 py-2 border-b border-white/10 font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500 flex items-center gap-2">
          <Scale className="w-3.5 h-3.5 text-amber-400" /> Docket · {data.cases.length}{canJudge && <span className="ml-auto text-amber-300 normal-case tracking-normal">bench access</span>}
        </p>
        {data.cases.map((c) => (
          <button key={c.id} data-testid={`mdt-docket-${c.docket}`} onClick={() => openCase(c.id)}
            className={`w-full text-left border-b border-white/5 px-3.5 py-3 transition-colors ${sel?.id === c.id ? "bg-amber-500/5 border-l-2 border-l-amber-400" : "hover:bg-white/[0.02]"}`}>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] font-bold text-amber-300">{c.docket}</span>
              <span className={`ml-auto font-mono text-[7.5px] uppercase border px-1.5 py-0.5 ${STATUS_CLS[c.status]}`}>{c.status.replace(/_/g, " ")}</span>
            </div>
            <p className="font-mono text-[10px] text-zinc-200 mt-1">The City v. {c.defendant?.name}</p>
            <p className="font-mono text-[8px] text-zinc-500 mt-0.5">{c.charges.length} charge(s){c.plea ? ` · plea: ${c.plea.replace(/_/g, " ")}` : ""}</p>
          </button>
        ))}
        {data.cases.length === 0 && <p className="p-4 font-mono text-[9px] text-zinc-500 uppercase tracking-[0.15em] text-center">Docket clear</p>}
      </aside>

      {/* case detail */}
      <main className="flex-1 min-w-0 overflow-y-auto p-4" style={{ scrollbarWidth: "thin" }}>
        {!sel ? (
          <div className="h-full flex items-center justify-center"><p className="font-mono text-[10px] text-zinc-600 uppercase tracking-[0.3em]">Select a case</p></div>
        ) : (
          <div data-testid="mdt-court-case" className="flex flex-col gap-4 max-w-xl">
            <div>
              <p className="font-mono text-[10px] text-amber-300">{sel.docket}{sel.arrest_ref ? ` · from ${sel.arrest_ref}` : ""}</p>
              <h2 className="font-bold text-lg mt-0.5 text-zinc-100">The City v. {sel.defendant?.name}</h2>
              <p className="font-mono text-[8.5px] text-zinc-500 uppercase mt-0.5">Arresting officer: {sel.arresting_officer || "—"} · #{sel.defendant?.playercode}</p>
            </div>
            <div>
              <p className="font-mono text-[8px] tracking-[0.25em] uppercase text-zinc-500 mb-1.5">Charges</p>
              {sel.charges.map((ch, i) => (
                <div key={i} data-testid={`mdt-charge-${i}`} className="border border-white/10 px-3 py-2 flex items-center gap-2.5 flex-wrap mb-1">
                  <span className={`font-mono text-[7.5px] uppercase border px-1.5 py-0.5 ${SEV_CLS[ch.severity]}`}>{ch.severity}</span>
                  <span className="font-mono text-[10px] text-zinc-100">{ch.title}</span>
                  <span className="ml-auto font-mono text-[8.5px] text-zinc-500 flex items-center gap-3">
                    {ch.fine > 0 && <span className="flex items-center gap-1"><Banknote className="w-3 h-3" />{fmt(ch.fine)}</span>}
                    {ch.jail_seconds > 0 && <span className="flex items-center gap-1"><Timer className="w-3 h-3" />{Math.round(ch.jail_seconds / 60)}min</span>}
                  </span>
                </div>
              ))}
            </div>
            {sel.evidence_summary && (
              <div>
                <p className="font-mono text-[8px] tracking-[0.25em] uppercase text-zinc-500 mb-1.5 flex items-center gap-1.5"><FileText className="w-3 h-3" /> Evidence / report narrative</p>
                <p className="font-mono text-[9.5px] text-zinc-400 leading-relaxed border border-white/10 bg-black/30 p-3">{sel.evidence_summary}</p>
              </div>
            )}
            <p className="font-mono text-[9px] uppercase text-zinc-400">Plea: <span className="text-zinc-200">{sel.plea ? sel.plea.replace(/_/g, " ") : "not entered"}</span></p>

            {sel.status === "verdict" && sel.verdict ? (
              <div className={`border p-4 ${sel.verdict.outcome === "guilty" ? "border-red-500/40 bg-red-500/5" : "border-emerald-500/40 bg-emerald-500/5"}`} data-testid="mdt-verdict">
                <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1.5"><Gavel className="w-3 h-3" /> Verdict — {sel.verdict.judge || "the court"}</p>
                <p className="font-bold text-base uppercase mt-1">{sel.verdict.outcome.replace(/_/g, " ")}</p>
                <p className="font-mono text-[9.5px] text-zinc-300 mt-1">
                  {sel.verdict.fine > 0 && <>Fine {fmt(sel.verdict.fine)} · </>}
                  {sel.verdict.jail_seconds > 0 && <>{Math.round(sel.verdict.jail_seconds / 60)}min custody</>}
                  {!sel.verdict.fine && !sel.verdict.jail_seconds && "No penalty imposed"}
                </p>
              </div>
            ) : canJudge && (
              <div className="border border-amber-500/30 bg-amber-500/5 p-3.5 flex flex-col gap-2.5" data-testid="mdt-bench">
                <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-amber-300 flex items-center gap-1.5"><Gavel className="w-3 h-3" /> Bench controls</p>
                {sel.status === "awaiting_hearing" && (
                  <button data-testid="mdt-hearing-start" disabled={busy} onClick={() => act("hearing", {})}
                    className="font-mono text-[9px] uppercase border border-sky-500/50 text-sky-300 px-3 py-2 hover:bg-sky-500/10 transition-colors w-fit">Call hearing to order</button>
                )}
                {sel.status === "in_hearing" && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <select data-testid="mdt-verdict-outcome" value={verdict.outcome} onChange={(e) => setVerdict({ ...verdict, outcome: e.target.value })} className="bg-black border border-white/15 px-2 py-2 font-mono text-[10px]">
                        <option value="guilty">Guilty</option><option value="not_guilty">Not guilty</option><option value="dismissed">Dismissed</option>
                      </select>
                      <input data-testid="mdt-verdict-judge" placeholder="Judge name" value={verdict.judge_name} onChange={(e) => setVerdict({ ...verdict, judge_name: e.target.value })} className="bg-black border border-white/15 px-2.5 py-2 font-mono text-[10px] focus:outline-none focus:ring-1 focus:ring-amber-500" />
                      <input data-testid="mdt-verdict-fine" inputMode="numeric" placeholder="Fine $" value={verdict.fine} onChange={(e) => setVerdict({ ...verdict, fine: e.target.value.replace(/\D/g, "") })} className="bg-black border border-white/15 px-2.5 py-2 font-mono text-[10px] focus:outline-none focus:ring-1 focus:ring-amber-500" />
                      <input data-testid="mdt-verdict-jail" inputMode="numeric" placeholder="Custody (sec)" value={verdict.jail_seconds} onChange={(e) => setVerdict({ ...verdict, jail_seconds: e.target.value.replace(/\D/g, "") })} className="bg-black border border-white/15 px-2.5 py-2 font-mono text-[10px] focus:outline-none focus:ring-1 focus:ring-amber-500" />
                    </div>
                    <input data-testid="mdt-verdict-notes" placeholder="Bench notes (optional)" value={verdict.notes} onChange={(e) => setVerdict({ ...verdict, notes: e.target.value })} className="bg-black border border-white/15 px-2.5 py-2 font-mono text-[10px] focus:outline-none focus:ring-1 focus:ring-amber-500" />
                    <button data-testid="mdt-verdict-deliver" disabled={busy} onClick={() => act("verdict", { ...verdict, fine: Number(verdict.fine || 0), jail_seconds: Number(verdict.jail_seconds || 0) })}
                      className="bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black font-mono text-[9px] uppercase tracking-[0.2em] px-4 py-2.5 transition-colors w-fit flex items-center gap-2">
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Gavel className="w-3.5 h-3.5" />} Deliver verdict
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
